import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import Bannerevent from "../public/banner-single-event.png";
import Navbar from '../components/navabar';
import Footer from '../components/footer';
import TicketSelectionModal from '../components/modal/TicketSelectionModal';
import { FiShare2, FiHeart, FiCalendar, FiMapPin, FiClock, FiTag, FiChevronDown } from 'react-icons/fi';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const buttonHover = {
  scale: 1.03,
  transition: { type: "spring", stiffness: 400, damping: 10 }
};

const buttonTap = {
  scale: 0.98
};

const SingleEvent = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch main event
        const eventResponse = await fetch(`https://event-li-backend.onrender.com/event/${id}`);
        if (!eventResponse.ok) {
          throw new Error('Event not found');
        }
        const eventData = await eventResponse.json();
        setEvent(eventData);

        // Fetch related events
        const relatedResponse = await fetch(`https://event-li-backend.onrender.com/event/${id}/related`);
        if (relatedResponse.ok) {
          const relatedData = await relatedResponse.json();
          setRelatedEvents(relatedData);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            {/* Banner Skeleton */}
            <div className="h-96 bg-gray-200 rounded-xl"></div>
            
            {/* Main Content Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-12 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                
                <div className="flex space-x-4">
                  <div className="h-10 bg-gray-200 rounded-full w-32"></div>
                  <div className="h-10 bg-gray-200 rounded-full w-32"></div>
                </div>
                
                {/* Details Skeleton */}
                <div className="space-y-8">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Ticket Sidebar Skeleton */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 sticky top-8">
                  <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
                  <div className="h-12 bg-gray-200 rounded-full w-full mt-6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-xl shadow-sm max-w-md mx-auto">
            <div className="text-red-500 text-2xl mb-4">Error</div>
            <p className="text-gray-700 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-xl shadow-sm max-w-md mx-auto">
            <div className="text-gray-600 text-2xl mb-4">Event Not Found</div>
            <p className="text-gray-500 mb-6">The event you're looking for doesn't exist or may have been removed.</p>
            <a 
              href="/events"
              className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition"
            >
              Browse Events
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Format date and time
  const eventDate = format(parseISO(event.startDate), 'EEEE, MMMM d, yyyy');
  const startTime = format(parseISO(event.startDate), 'h:mm a');
  const endTime = format(parseISO(event.endDate), 'h:mm a');

  // Calculate if event is sold out
  const isSoldOut = event.tickets && event.tickets.reduce((total, ticket) => total + ticket.quantity, 0) <= 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <motion.main 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Event Banner */}
        <motion.div 
          className="relative rounded-2xl overflow-hidden shadow-lg mb-12"
          variants={fadeIn}
        >
          <img 
            className="w-full h-96 object-cover"
            src={event.image || Bannerevent} 
            alt={event.title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
            <div>
              <motion.h1 
                className="text-4xl md:text-5xl font-bold text-white mb-2"
                variants={itemVariants}
              >
                {event.title}
              </motion.h1>
              <motion.p 
                className="text-xl text-white/90 max-w-3xl"
                variants={itemVariants}
              >
                {event.description}
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Actions */}
            <motion.div 
              className="flex flex-wrap gap-4"
              variants={itemVariants}
            >
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-700 hover:bg-gray-50 transition"
              >
                <FiHeart className="text-red-500" />
                <span>Save</span>
              </motion.button>
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-700 hover:bg-gray-50 transition"
              >
                <FiShare2 />
                <span>Share</span>
              </motion.button>
            </motion.div>

            {/* Event Details */}
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Event Details</h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="text-blue-600 mt-1">
                    <FiCalendar size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Date & Time</h3>
                    <p className="text-gray-600">{eventDate}</p>
                    <p className="text-gray-600">{startTime} - {endTime}</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="text-blue-600 mt-1">
                    <FiMapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Location</h3>
                    <p className="text-gray-600">
                      {event.locationType === 'online' ? (
                        <span className="text-blue-600">Online Event</span>
                      ) : (
                        event.location
                      )}
                    </p>
                    {event.locationType !== 'online' && (
                      <button className="text-blue-600 text-sm mt-1 hover:underline">
                        View on map
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="text-blue-600 mt-1">
                    <FiClock size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Duration</h3>
                    <p className="text-gray-600">
                      {Math.round((new Date(event.endDate) - new Date(event.startDate)) / (1000 * 60 * 60))} hours
                    </p>
                  </div>
                </div>
                
                {event.tags && event.tags.length > 0 && (
                  <div className="flex gap-4">
                    <div className="text-blue-600 mt-1">
                      <FiTag size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Tags</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {event.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* About Event */}
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-800">About this event</h2>
              <div className="prose max-w-none text-gray-700">
                {event.longDescription ? (
                  <p>{event.longDescription}</p>
                ) : (
                  <>
                    <p>Welcome to {event.title}! Join us for this special event {event.locationType === 'online' ? 'online' : 'in person'} where we come together to {event.description}.</p>
                    <p className="mt-4">This will be a powerful time of encountering God's presence through worship, prayer, and the Word. Whether you're joining alone or with your family, we believe this will be a transformative experience for all who participate.</p>
                  </>
                )}
              </div>
            </motion.div>

            {/* FAQ Section */}
            {event.faqs && event.faqs.length > 0 && (
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                variants={itemVariants}
              >
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {event.faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <button
                        onClick={() => toggleFaq(index)}
                        className="flex justify-between items-center w-full text-left py-2 focus:outline-none"
                      >
                        <h3 className="font-medium text-gray-800">{faq.question}</h3>
                        <FiChevronDown 
                          className={`text-gray-500 transition-transform ${expandedFaq === index ? 'transform rotate-180' : ''}`}
                        />
                      </button>
                      <AnimatePresence>
                        {expandedFaq === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <p className="text-gray-600 pt-2 pb-1">{faq.answer}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Organizer */}
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Organizer</h2>
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
                    {event.organizer?.charAt(0) || 'O'}
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {event.organizer || 'Event Organizer'}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    120 Followers • 5 Events
                  </p>
                  <p className="text-gray-600 mt-3">
                    We organize high-quality events that bring people together for meaningful experiences.
                  </p>
                  <div className="flex gap-4 mt-6">
                    <button className="px-6 py-2 border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition">
                      Contact
                    </button>
                    <button className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">
                      Follow
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Ticket Card */}
          <div className="lg:col-span-1">
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 sticky top-8"
              variants={itemVariants}
            >
              <div className="text-center mb-6">
                {event.ticketType === 'free' ? (
                  <span className="inline-block px-4 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Free Event
                  </span>
                ) : (
                  <div className="space-y-1">
                    <span className="text-3xl font-bold text-gray-900">
                      {event.tickets && event.tickets.length > 0 ? 
                        `From ₦${Math.min(...event.tickets.map(t => parseFloat(t.price.replace(/[^\d.-]/g, ''))).toLocaleString())}` : 
                        '₦20,000'}
                    </span>
                    {event.tickets && event.tickets.some(t => t.type === 'early-bird') && (
                      <span className="block text-sm text-gray-500">
                        Early bird pricing available
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {isSoldOut ? (
                  <div className="text-center py-4 bg-red-50 rounded-lg">
                    <p className="text-red-600 font-medium">This event is sold out</p>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsTicketModalOpen(true)}
                    className="w-full py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition flex items-center justify-center gap-2"
                  >
                    {event.ticketType === 'free' ? 'Get Free Tickets' : 'Select Tickets'}
                  </button>
                )}
                
                <div className="text-center text-sm text-gray-500">
                  {event.ticketType === 'free' ? (
                    <p>Registration required</p>
                  ) : (
                    <p>Prices may increase at any time</p>
                  )}
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-2">Available Tickets</h3>
                  <ul className="space-y-3">
                    {event.tickets && event.tickets.length > 0 ? (
                      event.tickets.slice(0, 3).map((ticket, index) => (
                        <li key={index} className="flex justify-between items-center">
                          <span className="text-gray-700">{ticket.name}</span>
                          <span className="font-medium">
                            {ticket.price === '0' || ticket.price === 'Free' ? 'Free' : `₦${parseFloat(ticket.price.replace(/[^\d.-]/g, '')).toLocaleString()}`}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500">General Admission - {event.ticketType === 'free' ? 'Free' : '₦20,000'}</li>
                    )}
                    {event.tickets && event.tickets.length > 3 && (
                      <li className="text-blue-600 text-sm">
                        +{event.tickets.length - 3} more ticket types
                      </li>
                    )}
                  </ul>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-2">Refund Policy</h3>
                  <p className="text-sm text-gray-600">
                    {event.ticketType === 'free' ? 
                      'Free event - no refund needed' : 
                      'Contact the organizer for refund requests. Refunds available up to 7 days before the event.'}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Related Events */}
        {relatedEvents.length > 0 && (
          <motion.section 
            className="mt-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.h2 variants={itemVariants} className="text-3xl font-bold mb-8 text-gray-800">
              You May Also Like
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedEvents.map((relatedEvent) => (
                <motion.div
                key={relatedEvent._id}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow relative group"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={relatedEvent.image || Bannerevent} 
                    alt={relatedEvent.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                {/* Small preview that's always visible */}
                <div className="p-4 group-hover:opacity-0 transition-opacity duration-300">
                  <h3 className="font-semibold text-lg mb-1 line-clamp-2">{relatedEvent.title}</h3>
                </div>
                
                {/* Full content that shows on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out">
                  <h3 className="font-semibold text-lg mb-1 line-clamp-2">{relatedEvent.title}</h3>
                  <p className="text-gray-500 text-sm mb-2">
                    {format(parseISO(relatedEvent.startDate), 'MMM d, yyyy')} •{' '}
                    {format(parseISO(relatedEvent.startDate), 'h:mm a')}
                  </p>
                  <p className="text-gray-500 text-sm mb-3 line-clamp-1">
                    {relatedEvent.locationType === 'online' ? 'Online' : relatedEvent.location}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">
                      {relatedEvent.ticketType === 'free' ? 'Free' : 
                        relatedEvent.tickets && relatedEvent.tickets.length > 0 ? 
                        `From ₦${Math.min(...relatedEvent.tickets.map(t => parseFloat(t.price.replace(/[^\d.-]/g, ''))).toLocaleString())}` : 
                        '₦20,000'}
                    </span>
                    <a 
                      href={`/event/${relatedEvent._id}`} 
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Event
                    </a>
                  </div>
                </div>
              </motion.div>
              ))}
            </div>
          </motion.section>
        )}
      </motion.main>

      {/* Ticket Selection Modal */}
      <TicketSelectionModal 
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
        eventId={id}
        eventDetails={event}
      />
      
      <Footer />
    </div>
  );
};

export default SingleEvent;